// Apple In-App Purchase (StoreKit 2) през expo-iap — САМО за iOS.
//
// Абонаментът на наемодателите/пропърти мениджърите в iOS приложението се
// купува през App Store (Guideline 3.1.1). Един автоматично подновяващ се
// продукт за всеки брой имоти (com.livupp.landlord.m{N}), всички в една
// група — така смяната на плана е StoreKit upgrade/downgrade.
//
// На Android и уеб всичко тук са безобидни заглушки, за да е безопасен
// import-ът от общите екрани.

import { Platform } from 'react-native';
import type * as ExpoIap from 'expo-iap';
import type { Purchase } from 'expo-iap';
import { t } from './i18n';

// Модулът се зарежда лениво и безопасно: по-стари iOS бинарни файлове (1.0.0)
// нямат нативния ExpoIap и статичен import би сринал приложението при OTA.
// Там IAP_SUPPORTED е false и екранът за абонамент се държи както преди.
let mod: typeof ExpoIap | null = null;
if (Platform.OS === 'ios') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    mod = require('expo-iap');
  } catch {
    mod = null;
  }
}

export const IAP_SUPPORTED = Platform.OS === 'ios' && !!mod;

// Префикс на нашите SKU-та (вж. GET /billing/apple/products)
export const APPLE_SKU_PREFIX = 'com.livupp.landlord.m';

export type IapProduct = {
  productId: string;
  /** Локализирана цена от StoreKit, напр. „5,99 €“ */
  displayPrice: string;
  title: string;
};

export type IapPurchase = {
  /** StoreKit transaction id — подава се на POST /billing/apple/verify */
  transactionId: string;
  productId: string;
  purchase: Purchase;
};

/** Грешка при отказ от потребителя (затворен лист за плащане) */
export function isCancelled(e: unknown): boolean {
  const code = (e as { code?: string } | null)?.code;
  return code === mod?.ErrorCode?.UserCancelled || code === 'user-cancelled' || code === 'E_USER_CANCELLED';
}

let connected = false;

export async function connect(): Promise<boolean> {
  if (!IAP_SUPPORTED) return false;
  if (connected) return true;
  connected = await mod!.initConnection();
  return connected;
}

export async function disconnect(): Promise<void> {
  if (!IAP_SUPPORTED || !connected) return;
  connected = false;
  try {
    await mod!.endConnection();
  } catch {
    // няма какво да направим — връзката така или иначе се затваря
  }
}

/** Зарежда абонаментните продукти от App Store по списък SKU-та. */
export async function loadProducts(skus: string[]): Promise<IapProduct[]> {
  if (!IAP_SUPPORTED || skus.length === 0) return [];
  await connect();
  const items = (await mod!.fetchProducts({ skus, type: 'subs' })) ?? [];
  return items.map((p) => ({ productId: p.id, displayPrice: p.displayPrice, title: p.title }));
}

function toIapPurchase(p: Purchase): IapPurchase {
  // В OpenIAP `id` е идентификаторът на транзакцията (StoreKit transaction id)
  const transactionId = (p as { transactionId?: string | null }).transactionId || p.id;
  return { transactionId: String(transactionId), productId: p.productId, purchase: p };
}

/**
 * Стартира покупка/смяна на абонамент. Резолва с транзакцията след като
 * Apple я потвърди. При отказ от потребителя хвърля грешка, за която
 * `isCancelled()` връща true. Ако покупката чака одобрение (Ask to Buy),
 * хвърля грешка с обяснение на български.
 */
export async function purchase(sku: string): Promise<IapPurchase> {
  if (!IAP_SUPPORTED) throw new Error(t('Покупките през App Store са налични само на iPhone/iPad.'));
  await connect();
  const result = await mod!.requestPurchase({ request: { apple: { sku } }, type: 'subs' });
  const list = Array.isArray(result) ? result : result ? [result] : [];
  // Търсим точно нашата транзакция (при upgrade StoreKit понякога връща и старата)
  const mine = list.find((p) => p.productId === sku) ?? list[0];
  if (!mine) throw new Error(t('Покупката не беше завършена. Опитайте отново.'));
  if (mine.purchaseState === 'pending') {
    throw new Error(t('Покупката чака одобрение (напр. „Поискай покупка“). Ще се активира след потвърждение.'));
  }
  return toIapPurchase(mine);
}

/**
 * Възстановяване: синхронизира с App Store и връща най-новата активна
 * транзакция от нашата абонаментна група (или null, ако няма такава).
 */
export async function restore(): Promise<IapPurchase | null> {
  if (!IAP_SUPPORTED) return null;
  await connect();
  try {
    await mod!.restorePurchases();
  } catch {
    // restorePurchases само опреснява; истинската проверка е долу
  }
  const purchases = await mod!.getAvailablePurchases({ onlyIncludeActiveItemsIOS: true, alsoPublishToEventListenerIOS: false });
  const ours = purchases
    .filter((p) => p.productId.startsWith(APPLE_SKU_PREFIX) && p.purchaseState !== 'pending')
    .sort((a, b) => (b.transactionDate ?? 0) - (a.transactionDate ?? 0));
  return ours[0] ? toIapPurchase(ours[0]) : null;
}

/** Приключва транзакцията СЛЕД като сървърът я е потвърдил. */
export async function finish(p: IapPurchase): Promise<void> {
  if (!IAP_SUPPORTED) return;
  try {
    await mod!.finishTransaction({ purchase: p.purchase, isConsumable: false });
  } catch {
    // Вече приключена/непозната транзакция — StoreKit ще я преизпрати при нужда
  }
}
